import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './Tasty.css';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'ytd', label: 'Year to Date' },
];

function Tasty({ onLogout }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('this_week');
  const [openCloseFilter, setOpenCloseFilter] = useState('all');
  const [buySellFilter, setBuySellFilter] = useState('all');
  const [symbolFilter, setSymbolFilter] = useState('');

  useEffect(() => {
    fetchTrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_URL}/portfolio/tastytrade-trades/`, { period });
      setTrades(Array.isArray(response.data.trades) ? response.data.trades : []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError('Error fetching tastytrade trades: ' + err.message);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrades = useMemo(() => {
    const symbol = symbolFilter.trim().toUpperCase();
    return trades.filter((trade) => {
      if (openCloseFilter !== 'all' && trade.open_close !== openCloseFilter) return false;
      if (buySellFilter !== 'all' && trade.buy_sell !== buySellFilter) return false;
      if (symbol) {
        const haystack = `${trade.underlying_symbol || ''} ${trade.symbol || ''}`.toUpperCase();
        if (!haystack.includes(symbol)) return false;
      }
      return true;
    });
  }, [trades, openCloseFilter, buySellFilter, symbolFilter]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = parseFloat(value);
    if (Number.isNaN(num)) return null;
    return num.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '-';
    return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const totalRealizedPnl = useMemo(() => {
    return filteredTrades.reduce((sum, trade) => {
      if (trade.open_close !== 'close' || trade.realized_pnl === null || trade.realized_pnl === undefined) {
        return sum;
      }
      return sum + parseFloat(trade.realized_pnl);
    }, 0);
  }, [filteredTrades]);

  const closesCount = filteredTrades.filter((t) => t.open_close === 'close').length;
  const opensCount = filteredTrades.filter((t) => t.open_close === 'open').length;

  return (
    <div className="tasty-container">
      <div className="tasty-header">
        <h2>Tasty</h2>
        <p>Tastytrade trades captured live via the account streamer</p>
      </div>

      <div className="tasty-content">
        <div className="tasty-controls">
          <div className="tasty-period-selector">
            <label htmlFor="tasty-period-select">Period: </label>
            <select
              id="tasty-period-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="tasty-open-close-selector">
            <label htmlFor="tasty-open-close-select">Type: </label>
            <select
              id="tasty-open-close-select"
              value={openCloseFilter}
              onChange={(e) => setOpenCloseFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="close">Close</option>
            </select>
          </div>
          <div className="tasty-buy-sell-selector">
            <label htmlFor="tasty-buy-sell-select">Side: </label>
            <select
              id="tasty-buy-sell-select"
              value={buySellFilter}
              onChange={(e) => setBuySellFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>
          <div className="tasty-symbol-filter">
            <label htmlFor="tasty-symbol-input">Symbol: </label>
            <input
              id="tasty-symbol-input"
              type="text"
              placeholder="Filter by symbol..."
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
            />
          </div>
        </div>

        {!loading && !error && (
          <div className="tasty-summary">
            <span><strong>{filteredTrades.length}</strong> trade{filteredTrades.length === 1 ? '' : 's'}</span>
            <span><strong>{opensCount}</strong> open{opensCount === 1 ? '' : 's'}</span>
            <span><strong>{closesCount}</strong> close{closesCount === 1 ? '' : 's'}</span>
            <span>
              Realized P&amp;L:{' '}
              <span className={totalRealizedPnl >= 0 ? 'positive' : 'negative'}>
                {formatCurrency(totalRealizedPnl) || '$0.00'}
              </span>
            </span>
          </div>
        )}

        {loading ? (
          <div className="tasty-loading">Loading trades...</div>
        ) : error ? (
          <div className="tasty-error">{error}</div>
        ) : filteredTrades.length === 0 ? (
          <div className="tasty-empty-state">No tastytrade trades found for this period.</div>
        ) : (
          <div className="tasty-table-container">
            <table className="tasty-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Filled At</th>
                  <th>Symbol</th>
                  <th>Type</th>
                  <th>Side</th>
                  <th>Open/Close</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>P&amp;L</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrades.map((trade) => {
                  const pnlKnown = trade.realized_pnl !== null && trade.realized_pnl !== undefined;
                  const pnlAmbiguous = !pnlKnown
                    && trade.open_close === 'close'
                    && trade.realized_pnl_day_cumulative !== null
                    && trade.realized_pnl_day_cumulative !== undefined;
                  const pnlValue = pnlKnown ? trade.realized_pnl : (pnlAmbiguous ? trade.realized_pnl_day_cumulative : null);
                  const pnlNum = pnlValue !== null ? parseFloat(pnlValue) : null;

                  return (
                    <tr key={trade.id}>
                      <td className="tasty-account-cell">{trade.account_display_name || trade.account_id}</td>
                      <td className="tasty-date-cell">{formatDateTime(trade.filled_at)}</td>
                      <td>
                        <div className="tasty-symbol-cell">{trade.symbol || '-'}</div>
                        {trade.underlying_symbol && trade.underlying_symbol !== trade.symbol && (
                          <div className="tasty-underlying-cell">{trade.underlying_symbol}</div>
                        )}
                      </td>
                      <td>{trade.instrument_type || '-'}</td>
                      <td className={`tasty-buy-sell-cell ${trade.buy_sell || ''}`}>{trade.buy_sell || '-'}</td>
                      <td>
                        {trade.open_close ? (
                          <span className={`tasty-open-close-badge ${trade.open_close}`}>{trade.open_close}</span>
                        ) : '-'}
                      </td>
                      <td className="tasty-number-cell">{formatNumber(trade.quantity, 0)}</td>
                      <td className="tasty-number-cell">{formatNumber(trade.price, 2)}</td>
                      <td className={`tasty-number-cell ${pnlNum === null ? 'muted' : pnlNum >= 0 ? 'positive' : 'negative'}`}>
                        {pnlNum === null ? '-' : formatCurrency(pnlNum)}
                        {pnlAmbiguous && <span className="tasty-pnl-note">day total, not isolated</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Tasty;
